// UserBbsInfoView.js
import { useState } from 'react';
import { styled  } from '@mui/material/styles';
import { Box } from '@mui/system';
import {
    DialogContent, DialogActions, Button, Popover, ButtonGroup
} from '@mui/material';
import DoneIcon from '@mui/icons-material/Done';
import EditIcon from '@mui/icons-material/Edit';
import CampaignIcon from '@mui/icons-material/Campaign';
import { AppStyle, AppWord, CodeGroup } from '../../../value/AppConst';
import { useReactiveVar } from '@apollo/client';
import { authTableTreeRepo, codeTreeRepo, userInfoRepo } from '../../../value/AppModel';
import UserBbsComments from './UserBbsComments';
import { VzmDataText, VzmInputFlex, VzmLabel } from '../../../common/VzmStyled';
import UserBbsFiles from './UserBbsFiles';
import { useAuthorizationCheck } from '../../../api/app-hook';
import ValueMisc from '../../../value/ValueMisc';
import { useConfirmDialog } from '../../../common/AppConfirmDialog';
import DraggableViewer from '../../../common/DraggableViewer';


// In ViewAndComments (left side)
const ViewContainer = styled(Box)({
    flexGrow: 1, fontSize: '0.9rem', marginRight: 15
});

// show new lines in text (as is)
const TextBox = styled(Box)({
    padding: 15, borderRadius: 10,
    margin: '10px 0px',
    background: 'linear-gradient(#f0f0f0 0%, #ffffff 20%, #ffffff 80%, #f0f0f0 100%)',
    whiteSpace: 'pre-line',
    minHeight: 250, overflowY: 'auto',
    maxHeight: '40vh', boxSizing: 'border-box',
});

const FilesAndComments = styled(Box)({
    display: 'flex', // marginTop: 10,
    gap: 20,
});

/**
 * 사용자 게시판 상세 보기
 * @param {*} param0
 * @returns
 */
export default function UserBbsInfoView({
    open,
    item,
    onRequestEdit,
    onClose,
    onRequestReload,
    onRequestSetStatus
}) {
    const [anchorEl, setAnchorEl] = useState(null);
    const openConfirmDialog = useConfirmDialog();
    const sessionInfo = useReactiveVar(userInfoRepo);
    const authTableTree = useReactiveVar(authTableTreeRepo);
    const codeTree = useReactiveVar(codeTreeRepo);
    const checkAuth = useAuthorizationCheck();

    const windowWidth = window.innerWidth;
    const StatusList =  ValueMisc.getCodes(codeTree, CodeGroup.USER_BBS_STATUS_CD);
    const canSetStatus = checkAuth(authTableTree.tree.pr_com_info_set_dc);

    const onClickSetStatus = async (newStatus) => {
        setAnchorEl(null);

        const confirm = await openConfirmDialog({
            title: '상태 변경',
            content:['게시물 상태를 변경하시겠습니까?'],
        });

        if(confirm) {
            onRequestSetStatus(item.bbsInfoSeq, newStatus);
        }
    };

    if(!open || !item) return null;

    return(
        <DraggableViewer open={open} onClose={onClose} maxWidth='xl'
            startIcon={<CampaignIcon fontSize={AppWord.LARGE} />}
            title={item.title}
            fullScreen={windowWidth < 1200}
        >
            <DialogContent>
                <ViewContainer
                    style={{
                        minWidth: windowWidth > 1200 ? 1100 : windowWidth - 100,
                    }}
                >
                    <TextBox>
                        {item.content}
                    </TextBox>
                    <VzmInputFlex
                        style={{justifyContent: 'space-between'}}
                    >
                        <VzmInputFlex>
                            <VzmLabel>작성자</VzmLabel>
                            <VzmDataText>{item.regUserName}</VzmDataText>
                        </VzmInputFlex>
                        <VzmInputFlex>
                            <VzmLabel>최종수정</VzmLabel>
                            <VzmDataText>{item.updateTime}</VzmDataText>
                        </VzmInputFlex>
                        <VzmInputFlex>
                            <VzmLabel>처리상태</VzmLabel>
                            <VzmDataText>{item.userBbsStatus}</VzmDataText>
                            {
                                canSetStatus &&
                                <>
                                <Button
                                    variant={AppStyle.mui.outlined}
                                    style={{marginLeft:10}}
                                    size={AppWord.SMALL}
                                    onClick={(e)=>{setAnchorEl(e.currentTarget);}}
                                >
                                    상태변경
                                </Button>
                                <Popover
                                    open={Boolean(anchorEl)}
                                    anchorEl={anchorEl}
                                    onClose={()=>{setAnchorEl(null);}}
                                    anchorOrigin={{
                                        vertical: 'bottom',
                                        horizontal: 'left',
                                    }}
                                    transformOrigin={{
                                        vertical: 'bottom'
                                    }}
                                >
                                    <ButtonGroup
                                        orientation="vertical"
                                    >
                                        {
                                            StatusList.map(status=>(
                                                <Button
                                                    key={status.codeId}
                                                    onClick={()=>{
                                                        onClickSetStatus(status.codeId);
                                                    }}
                                                    disabled={status.codeId === item.userBbsStatusCd}
                                                >
                                                    {status.codeName}
                                                </Button>
                                            ))
                                        }
                                    </ButtonGroup>
                                </Popover>
                                </>
                            }
                        </VzmInputFlex>
                    </VzmInputFlex>
                </ViewContainer>
                <FilesAndComments
                    style={{flexDirection: windowWidth > 720 ? 'row' : 'column'}}
                >
                    <UserBbsFiles
                        item={item}
                        onRequestReload={onRequestReload}
                    />
                    <UserBbsComments
                        userBbsInfo={item}
                    />
                </FilesAndComments>
            </DialogContent>
            <DialogActions>
                {
                    sessionInfo.user.userId <= item.regUserId ?
                    <Button
                        onClick={()=>{onRequestEdit(item)}}
                        variant={AppStyle.mui.contained}
                        color={AppStyle.mui.inherit}
                        sx={{width: 120, marginRight: 1}}
                        startIcon={<EditIcon />}
                    >
                        편집
                    </Button>
                    : null
                }
                <Button onClick={onClose}
                    variant={AppStyle.mui.contained}
                    color={AppStyle.mui.primary}
                    sx={{width: 120}}
                    startIcon={<DoneIcon />}
                >
                    닫기
                </Button>
            </DialogActions>
        </DraggableViewer>
    );
}